import { useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";

const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB — safely under Cloudflare's 100 MB limit
const DIRECT_UPLOAD_THRESHOLD = 8 * 1024 * 1024; // Files > 8 MB → chunked path

/**
 * VideoField — upload an MP4/WebM/MOV up to 200 MB.
 * Uses chunked upload when the file is large enough to risk hitting the
 * Cloudflare / proxy 100 MB per-request limit on Production.
 */
export default function VideoField({ value, onChange, label, testid }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(""); // "uploading" / "finalizing"

  const directUpload = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await http.post("/upload/video", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      },
    });
    return data;
  };

  const chunkedUpload = async (file) => {
    setStage("uploading");
    // 1. init
    const { data: initData } = await http.post("/upload/video/init", {
      filename: file.name,
      content_type: file.type || "video/mp4",
      expected_chunks: Math.ceil(file.size / CHUNK_SIZE),
    });
    const uploadId = initData.upload_id;
    const total = Math.ceil(file.size / CHUNK_SIZE);
    // 2. chunks (in-order, with retries for mobile network blips)
    for (let i = 0; i < total; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(file.size, start + CHUNK_SIZE);
      const blob = file.slice(start, end);
      let lastErr = null;
      let success = false;
      for (let attempt = 1; attempt <= 4 && !success; attempt++) {
        const form = new FormData();
        form.append("upload_id", uploadId);
        form.append("index", String(i));
        form.append("total", String(total));
        form.append("chunk", blob, `chunk-${i}`);
        try {
          await http.post("/upload/video/chunk", form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          success = true;
        } catch (e) {
          lastErr = e;
          // Wait with exponential backoff before retry: 1s, 2s, 4s
          if (attempt < 4) await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
        }
      }
      if (!success) throw lastErr || new Error("Chunk upload failed");
      setProgress(Math.round(((i + 1) / total) * 100));
    }
    // 3. complete (long timeout — GridFS write of full file)
    setStage("finalizing");
    const { data } = await http.post("/upload/video/complete", { upload_id: uploadId }, {
      timeout: 180000,
    });
    return data;
  };

  const upload = async (file) => {
    if (file.size > 200 * 1024 * 1024) { toast.error("Video must be ≤ 200 MB"); return; }
    setBusy(true); setProgress(0); setStage("");
    try {
      const data = file.size > DIRECT_UPLOAD_THRESHOLD
        ? await chunkedUpload(file)
        : await directUpload(file);
      onChange?.(data.url);
      toast.success(`Video uploaded (${(data.size_bytes/1024/1024).toFixed(1)} MB)`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally { setBusy(false); setProgress(0); setStage(""); }
  };

  const buttonLabel = busy
    ? (stage === "finalizing" ? "Finalizing…" : `${progress}%`)
    : "Upload";

  return (
    <div data-testid={`video-field-${testid || ""}`}>
      {label && <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">{label}</div>}
      <div className="flex gap-2 items-stretch">
        <input
          className="stc-input flex-1 font-mono text-[12px]"
          placeholder="Paste video URL or upload →"
          value={value || ""}
          onChange={e => onChange?.(e.target.value)}
          data-testid={`video-input-${testid || ""}`}
        />
        <label className={`btn-outline text-xs cursor-pointer flex items-center ${busy ? "opacity-50" : ""}`} data-testid={`video-upload-${testid || ""}`}>
          <Upload size={12}/> {buttonLabel}
          <input type="file" accept="video/*" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0])} disabled={busy}/>
        </label>
        {value && (
          <button type="button" onClick={() => onChange?.("")} className="btn-outline text-xs border-red-700 text-red-400" aria-label="Clear" data-testid={`video-clear-${testid || ""}`}><Trash2 size={12}/></button>
        )}
      </div>
      {value && (
        <div className="mt-2 inline-block">
          <video src={resolveImage(value)} className="max-h-48 border border-white/10" controls preload="metadata" />
        </div>
      )}
    </div>
  );
}
